// frontend/src/pages/WorkshopInvoicesPage.js
// Invoices the workshop has issued. Chrome/ink, French. Unpaid first — that's
// the only thing on this page that needs acting on.

import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import WorkshopQuotationInvoiceForm from "../components/WorkshopQuotationInvoiceForm";
import MarkInvoicePaidButton from "../components/MarkInvoicePaidButton";
import RelanceClientButton from "../components/RelanceClientButton";
import API_URL from "../api/baseURL";
import { generateDocumentPDF } from "../utils/generateDocumentPDF";
import {
  Page, Panel, Badge, Table, Tr, Td, Button,
  EmptyState, Loading, ErrorNote,
} from '../components/ui/Iparc';
import { fmt } from '../theme/iparc';

export default function WorkshopInvoicesPage({ token: propToken }) {
  const { token: contextToken, user } = useAuth();
  const token = propToken || contextToken;

  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [workshopData, setWorkshopData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState('unpaid');
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Session expirée — reconnectez-vous.");
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const [cl, ve, inx, ws] = await Promise.allSettled([
          fetch(`${API_URL}/users?role=car owner`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => {
            if (!res.ok) throw new Error("Échec du chargement des clients");
            return res.json();
          }),
          fetch(`${API_URL}/vehicles/all`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => {
            if (!res.ok) throw new Error("Échec du chargement des véhicules");
            return res.json();
          }),
          fetch(`${API_URL}/workshop-invoices/invoice/list`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => {
            if (!res.ok) throw new Error("Échec du chargement des factures");
            return res.json();
          }),
          fetch(`${API_URL}/workshops/${user?._id}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => res.json()),
        ]);
        if (cl.status === "fulfilled") setClients(cl.value);
        if (ve.status === "fulfilled") setVehicles(ve.value);
        if (inx.status === "fulfilled") setInvoices(inx.value);
        if (ws.status === "fulfilled") setWorkshopData(ws.value);
        setError(inx.status === "rejected" ? (inx.reason?.message || "Chargement impossible") : "");
      } catch (err) {
        setError(err.message || "Chargement impossible");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, user, refresh]);

  const handleRefresh = () => setRefresh(x => !x);

  const unpaid = useMemo(() => invoices.filter(i => !i.paid), [invoices]);
  const unpaidTotal = useMemo(
    () => unpaid.reduce((s, i) => s + Number(i.clientTotal || i.total || 0), 0),
    [unpaid]
  );

  const shown = useMemo(() => {
    const list = filter === 'unpaid' ? unpaid
      : filter === 'paid' ? invoices.filter(i => i.paid)
      : invoices;
    // Unpaid first, then most recent.
    return [...list].sort((a, b) =>
      (Number(a.paid) - Number(b.paid)) ||
      (new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    );
  }, [invoices, unpaid, filter]);

  const chips = [
    { key: 'unpaid', label: `Impayées · ${unpaid.length}` },
    { key: 'paid', label: `Réglées · ${invoices.length - unpaid.length}` },
    { key: 'all', label: `Toutes · ${invoices.length}` },
  ];

  return (
    <Page
      title="Factures"
      subtitle="Atelier"
      actions={
        <>
          <Button onClick={handleRefresh}>Actualiser</Button>
          <Button variant="primary" onClick={() => setShowForm(v => !v)}>
            {showForm ? 'Fermer le formulaire' : 'Nouvelle facture'}
          </Button>
        </>
      }
    >
      {error && <ErrorNote>{error}</ErrorNote>}

      {/* One number worth showing on this page: what is still owed. */}
      <Panel>
        <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C] mb-1.5">
          Encours client
        </p>
        <p className="font-['Space_Grotesk',sans-serif] tabular-nums text-[30px] font-semibold tracking-[-.02em] leading-none"
           style={{ color: unpaidTotal > 0 ? '#E2574B' : '#74A98D' }}>
          {fmt(unpaidTotal)} <span className="text-[15px] text-[#6E665C]">F</span>
        </p>
        <p className="text-[11.5px] text-[#8F877B] mt-2">
          {unpaid.length === 0
            ? 'Toutes les factures sont réglées.'
            : `${unpaid.length} facture${unpaid.length > 1 ? 's' : ''} en attente de règlement.`}
        </p>
      </Panel>

      {showForm && (
        <WorkshopQuotationInvoiceForm
          type="invoice"
          token={token}
          clients={clients}
          vehicles={vehicles}
          onSubmitted={() => { setShowForm(false); handleRefresh(); }}
        />
      )}

      <Panel
        title="Factures émises"
        bodyClass=""
        action={
          <div className="flex gap-[5px]">
            {chips.map(c => (
              <button
                key={c.key}
                onClick={() => setFilter(c.key)}
                className={`h-[26px] px-2.5 rounded-[7px] text-[11px] font-medium transition-colors ${
                  filter === c.key ? 'bg-[#241F1B] text-[#F4F1EA]' : 'text-[#6E665C] hover:text-[#C9C3B6]'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        }
      >
        {loading ? (
          <Loading label="Chargement des factures…" />
        ) : invoices.length === 0 ? (
          <EmptyState
            title="Aucune facture"
            hint="Une facture est créée automatiquement quand un devis est confirmé, ou manuellement ici."
            action={<Button variant="primary" onClick={() => setShowForm(true)}>Nouvelle facture</Button>}
          />
        ) : shown.length === 0 ? (
          <EmptyState title="Aucune facture dans cette vue." />
        ) : (
          <Table head={['Client', 'Véhicule', 'Total', 'Règlement', '']} minWidth={820}>
            {shown.map((inv) => {
              const clientTotal = Number(inv.clientTotal || inv.total || 0);
              const workshopTotal = Number(inv.total || 0);
              return (
                <Tr key={inv._id}>
                  <Td className="text-[#F4F1EA]">
                    {inv.client?.name || '—'}
                    {inv.documentNumber && (
                      <span className="block text-[11px] text-[#6E665C] mt-0.5 font-['Space_Grotesk',sans-serif]">
                        {inv.documentNumber}
                      </span>
                    )}
                  </Td>
                  <Td>
                    <span className="font-['Space_Grotesk',sans-serif] text-[12.5px] tracking-[.02em] text-[#F4F1EA]">
                      {inv.vehicle?.plateNumber || '—'}
                    </span>
                  </Td>
                  <Td mono>
                    {fmt(clientTotal)} <span className="text-[10.5px] text-[#6E665C]">F</span>
                    {workshopTotal > 0 && workshopTotal !== clientTotal && (
                      <span className="block text-[10.5px] text-[#6E665C] font-normal mt-0.5">
                        atelier {fmt(workshopTotal)} F
                      </span>
                    )}
                  </Td>
                  <Td>
                    <Badge tone={inv.paid ? 'ok' : 'danger'}>{inv.paid ? 'Réglée' : 'Impayée'}</Badge>
                  </Td>
                  <Td className="text-right whitespace-nowrap">
                    {!inv.paid && (
                      <>
                        {/* Chase first, settle second — that is the order the
                            counter works in on an unpaid invoice. */}
                        <span className="mr-1.5 inline-block align-middle">
                          <RelanceClientButton
                            invoiceId={inv._id}
                            token={token}
                            lastReminderAt={inv.lastReminderAt}
                            reminderCount={inv.reminderCount}
                          />
                        </span>
                        <span className="mr-1.5 inline-block align-middle">
                          <MarkInvoicePaidButton
                            invoiceId={inv._id}
                            token={token}
                            onMarked={handleRefresh}
                          />
                        </span>
                      </>
                    )}
                    <Button
                      variant="quiet"
                      className="h-[26px] px-2"
                      onClick={() => generateDocumentPDF(inv, 'invoice', workshopData)}
                      title="Télécharger la facture en PDF"
                    >
                      PDF
                    </Button>
                  </Td>
                </Tr>
              );
            })}
          </Table>
        )}
      </Panel>
    </Page>
  );
}
